import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import HubspotForm from './HubSpotForm';
import HeroVisual from './HeroVisual';
import ScrollReveal from './ScrollReveal';

function Home({ setPage }) {
    return (
        <main>
            {/* ===== HERO ===== */}
            <section className="site-section-lg">
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col lg={10} xl={8}>
                            <ScrollReveal animation="fade-in">
                                <span className="badge-ea mb-4 d-inline-block">Early Access</span>
                            </ScrollReveal>
                            {/* PREVIOUS HERO COPIES (preserved for team review):
                                V0 (original):
                                  Headline: "AI builds the code. QCoda makes sure it builds the right thing."
                                  Sub: "Product orchestration for teams where alignment matters more than speed.
                                        Feature requests become validated specifications and traceable code — with
                                        humans in the loop at every gate."
                                V1 (alignment layer):
                                  Headline: "The alignment layer between product intent and engineering execution."
                                  Sub: "Enhanced specs. Human or AI code. Rigorous validation. Trusted
                                        deployment. For new projects and existing codebases — with humans
                                        in the loop at every gate."
                            */}
                            <ScrollReveal animation="fade-up" delay={100}>
                                <h1 className="hero-headline mb-4">
                                    Velocity without the trust tax.
                                </h1>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={250}>
                                <p className="hero-sub mx-auto mb-4">
                                    QCoda enriches specs, validates alignment, and traces every decision
                                    from business intent to deployed code. Let AI write it or own every
                                    line yourself — either way, delivery accelerates and transparency is
                                    built in, not bolted on.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={400}>
                                <div className="d-flex gap-3 justify-content-center flex-wrap">
                                    <a href="#waitlist" className="btn-primary-cta">Join the Waitlist</a>
                                    <button onClick={() => setPage('contact')} className="btn-secondary-cta">
                                        Request a Demo
                                    </button>
                                </div>
                            </ScrollReveal>

                            {/* --- Value Prop Indicators --- */}
                            <ScrollReveal animation="fade-up" delay={550}>
                                <div className="hero-value-row">
                                    <div className="hero-value-item">
                                        <div className="hero-value-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="hero-value-title">Alignment Before Code</p>
                                            <p className="hero-value-desc">Ambiguous requirements get flagged before the pipeline runs.</p>
                                        </div>
                                    </div>
                                    <div className="hero-value-item">
                                        <div className="hero-value-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                <polyline points="14 2 14 8 20 8" />
                                                <line x1="16" y1="13" x2="8" y2="13" />
                                                <line x1="16" y1="17" x2="8" y2="17" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="hero-value-title">Decision Traceability</p>
                                            <p className="hero-value-desc">Every decision ledgered with date, rationale, and outcome.</p>
                                        </div>
                                    </div>
                                    <div className="hero-value-item">
                                        <div className="hero-value-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                                <circle cx="9" cy="7" r="4" />
                                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="hero-value-title">Dual Ownership</p>
                                            <p className="hero-value-desc">Product owns what and why. Engineering owns how. Zero ambiguity.</p>
                                        </div>
                                    </div>
                                </div>
                            </ScrollReveal>

                            <HeroVisual />
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== THE PROBLEM ===== */}
            <section className="site-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8} xl={7}>
                            <ScrollReveal animation="fade-up">
                                <h2 className="section-heading mb-4">
                                    The faster you build, the faster you build the wrong thing.
                                </h2>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <p className="section-subheading mb-3">
                                    AI coding tools made engineering cheaper. They didn't make alignment easier.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={200}>
                                <p className="section-subheading mb-3">
                                    Feature requests still get misinterpreted. Specs drift between what product
                                    intended and what engineering delivered. Decisions live in Slack threads,
                                    meeting notes, and someone's memory. When something ships wrong, nobody can trace why.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={300}>
                                <p className="section-subheading">
                                    The bottleneck was never build speed. It's decision quality — and right now,
                                    nobody owns it.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* ===== TRANSITION VISUAL ===== */}
            <ScrollReveal animation="fade-in">
                <div className="transition-visual">
                    <div className="transition-dots">
                        <span className="transition-dot" />
                        <span className="transition-dot" />
                        <span className="transition-dot" />
                        <span className="transition-dot" />
                        <span className="transition-dot" />
                    </div>
                    <span className="transition-arrow">&rarr;</span>
                    <div className="transition-dots">
                        <span className="transition-dot active" />
                        <span className="transition-dot active" style={{ animationDelay: '0.3s' }} />
                        <span className="transition-dot active" style={{ animationDelay: '0.6s' }} />
                    </div>
                </div>
            </ScrollReveal>

            {/* ===== THE SOLUTION ===== */}
            <section className="site-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8} xl={7}>
                            <ScrollReveal animation="fade-up">
                                <h2 className="section-heading mb-4">
                                    One orchestration layer between product intent and engineering execution.
                                </h2>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={100}>
                                <p className="section-subheading mb-3">
                                    QCoda takes plain-text feature requests and runs them through validation
                                    gates before a single line of code is written. Concerns are surfaced early.
                                    Alignment is verified against your product foundation. Decisions are
                                    ledgered — not lost.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={200}>
                                <p className="section-subheading mb-3">
                                    When a feature is cleared for build, the system generates code that maps
                                    directly to the validated specification. Every commit traces back to a
                                    decision. Every decision traces back to a rationale.
                                </p>
                            </ScrollReveal>
                            <ScrollReveal animation="fade-up" delay={300}>
                                <p className="section-subheading">
                                    Humans decide. AI executes. Nothing ships without alignment.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== VALUE PILLARS ===== */}
            <section className="site-section">
                <Container>
                    <Row className="justify-content-center g-4">
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={0} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="pillar-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Alignment Before Code
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Ambiguous requirements get flagged before the pipeline burns
                                        a half hour on the wrong problem. No silent misinterpretation.
                                        Concerns are raised and resolved before they become bugs.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={150} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="pillar-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                            <polyline points="14 2 14 8 20 8" />
                                            <line x1="16" y1="13" x2="8" y2="13" />
                                            <line x1="16" y1="17" x2="8" y2="17" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Full Decision Traceability
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Every product decision is ledgered with date, rationale, and outcome.
                                        Your institutional memory doesn't live in someone's head — or in a
                                        Slack thread from six months ago.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={300} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="pillar-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                            <circle cx="9" cy="7" r="4" />
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Dual Ownership, Zero Ambiguity
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Product owns what and why. Engineering owns how. The system
                                        owns when and where. Two roles, clear accountability, no politics.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* ===== WORKS WHERE YOU WORK ===== */}
            <section className="site-section integration-section">
                <Container>
                    <Row className="justify-content-center text-center mb-4">
                        <Col lg={8} xl={7}>
                            <ScrollReveal animation="fade-up">
                                <h2 className="section-heading mb-3">
                                    Works where your team already works.
                                </h2>
                                <p className="section-subheading">
                                    QCoda is an orchestration layer, not another destination. It plugs into the
                                    AI coding tools your developers already use and accepts work items from the
                                    PM systems your product team lives in. Nobody changes how they work — they
                                    just get alignment for free.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-4">
                        <Col sm={6} md={4}>
                            <ScrollReveal animation="fade-up" delay={0} className="h-100">
                                <div className="integration-card h-100">
                                    <div className="integration-icon">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="16 18 22 12 16 6" />
                                            <polyline points="8 6 2 12 8 18" />
                                        </svg>
                                    </div>
                                    <p className="integration-title">Your IDE & AI Tools</p>
                                    <p className="integration-desc">
                                        Developers stay in their editor. QCoda surfaces validated specs and
                                        traceability directly inside your AI coding workflow.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col sm={6} md={4}>
                            <ScrollReveal animation="fade-up" delay={150} className="h-100">
                                <div className="integration-card h-100">
                                    <div className="integration-icon">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="3" width="7" height="7" />
                                            <rect x="14" y="3" width="7" height="7" />
                                            <rect x="14" y="14" width="7" height="7" />
                                            <rect x="3" y="14" width="7" height="7" />
                                        </svg>
                                    </div>
                                    <p className="integration-title">Your PM System</p>
                                    <p className="integration-desc">
                                        Feature requests flow in from the tools product already uses.
                                        QCoda enriches them in place — no copy-paste, no context loss.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col sm={6} md={4}>
                            <ScrollReveal animation="fade-up" delay={300} className="h-100">
                                <div className="integration-card h-100">
                                    <div className="integration-icon">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="3" />
                                            <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                                        </svg>
                                    </div>
                                    <p className="integration-title">Your Inference Stack</p>
                                    <p className="integration-desc">
                                        Bring your own LLM or use platform defaults. Choose where your
                                        data goes — cloud, on-prem, or open-weight models you host.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* ===== INFRASTRUCTURE SIGNAL ===== */}
            <ScrollReveal animation="fade-in">
                <div className="infra-signal">
                    Powered by <a href="https://llmesh.net" target="_blank" rel="noopener noreferrer" className="infra-link">LLMesh</a> — our open-source inference broker for AI agents at scale. Proven with small open-weight models in production — not just frontier APIs.
                </div>
            </ScrollReveal>

            <hr className="section-divider" />

            {/* ===== TEAM ===== */}
            <section className="site-section">
                <Container>
                    <ScrollReveal animation="fade-up">
                        <Row className="justify-content-center text-center mb-4">
                            <Col>
                                <h2 className="section-heading mb-2">Built by founders who've done this before.</h2>
                            </Col>
                        </Row>
                    </ScrollReveal>
                    <Row className="justify-content-center g-4">
                        <Col md={4} className="text-center">
                            <ScrollReveal animation="scale-in" delay={0}>
                                <div className="glass-card h-100 d-flex flex-column align-items-center">
                                    <a href="https://www.linkedin.com/in/aschwabe/" target="_blank" rel="noopener noreferrer">
                                        <img src="/team/andrew_schwabe.jpg" alt="Andrew Schwabe" className="team-photo mb-3" />
                                    </a>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <p className="team-name">Andrew Schwabe</p>
                                        <a href="https://www.linkedin.com/in/aschwabe/" target="_blank" rel="noopener noreferrer" className="linkedin-icon" aria-label="LinkedIn">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                                <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z" />
                                            </svg>
                                        </a>
                                    </div>
                                    <p className="team-title mb-2">CEO</p>
                                    <p className="team-bio">Serial entrepreneur. Founded ForMotiv (behavioral analytics) and Saigon AI.</p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={4} className="text-center">
                            <ScrollReveal animation="scale-in" delay={150}>
                                <div className="glass-card h-100 d-flex flex-column align-items-center">
                                    <a href="https://www.linkedin.com/in/joyrebecca/" target="_blank" rel="noopener noreferrer">
                                        <img src="/team/rebecca_joy.jpg" alt="Rebecca Joy" className="team-photo mb-3" />
                                    </a>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <p className="team-name">Rebecca Joy</p>
                                        <a href="https://www.linkedin.com/in/joyrebecca/" target="_blank" rel="noopener noreferrer" className="linkedin-icon" aria-label="LinkedIn">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                                <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z" />
                                            </svg>
                                        </a>
                                    </div>
                                    <p className="team-title mb-2">Co-Founder, Finance & Operations</p>
                                    <p className="team-bio">Six-time co-founder. CEO of Saigon AI. UC Berkeley data science.</p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={4} className="text-center">
                            <ScrollReveal animation="scale-in" delay={300}>
                                <div className="glass-card h-100 d-flex flex-column align-items-center">
                                    <a href="https://www.linkedin.com/in/gwencheylan/" target="_blank" rel="noopener noreferrer">
                                        <img src="/team/gwen_cheylan.jpg" alt="Gwen Cheylan" className="team-photo mb-3" />
                                    </a>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <p className="team-name">Gwen Cheylan</p>
                                        <a href="https://www.linkedin.com/in/gwencheylan/" target="_blank" rel="noopener noreferrer" className="linkedin-icon" aria-label="LinkedIn">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                                <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z" />
                                            </svg>
                                        </a>
                                    </div>
                                    <p className="team-title mb-2">Co-Founder, Strategy & Market Positioning</p>
                                    <p className="team-bio">Four-time founder. CEO of Talential. Former CTO of Kiwipay.</p>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== RECENTLY SHIPPED + BLOG ===== */}
            <section className="site-section">
                <Container>
                    <Row className="justify-content-center mb-5">
                        <Col lg={6}>
                            <ScrollReveal animation="fade-up">
                                <p className="text-dim text-center mb-3" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                                    Recently Shipped
                                </p>
                                <div className="recently-shipped">
                                    <div className="shipped-item">
                                        <span className="shipped-dot" />
                                        <span>Full accept-to-merge loop — from validated spec to merged PR in one platform</span>
                                    </div>
                                    <div className="shipped-item">
                                        <span className="shipped-dot" />
                                        <span>IDE integration — QCoda surfaces specs directly inside your AI coding workflow</span>
                                    </div>
                                    <div className="shipped-item">
                                        <span className="shipped-dot" />
                                        <span>Bring-your-own-LLM endpoint manager with org-scoped data isolation</span>
                                    </div>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center">
                        <Col lg={6}>
                            <ScrollReveal animation="fade-up">
                                <p className="text-dim text-center mb-3" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                                    From Our Blog
                                </p>
                                <a
                                    href="https://hackernoon.com/alignment-driven-development-the-orchestration-framework-ai-paired-coding-forgot-to-build"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="blog-card"
                                >
                                    <p className="blog-card-title">Alignment-Driven Development</p>
                                    <p className="blog-card-subtitle">The orchestration framework AI-paired coding forgot to build.</p>
                                    <span className="blog-card-link">Read on Hackernoon &rarr;</span>
                                </a>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== ENTERPRISE / TRUST ===== */}
            <section className="site-section">
                <Container>
                    <Row className="justify-content-center text-center mb-4">
                        <Col lg={8} xl={7}>
                            <ScrollReveal animation="fade-up">
                                <h2 className="section-heading mb-3">
                                    Built for Teams That Can't Compromise
                                </h2>
                                <p className="section-subheading">
                                    QCoda is designed from the ground up for serious product teams and
                                    enterprises that demand control, security, and uncompromising quality.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center g-4">
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={0} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="pillar-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Data Sovereignty
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Bring your own LLM endpoint. Choose exactly where your code and
                                        specifications are processed — whether it's your private Azure OpenAI,
                                        AWS Bedrock, Anthropic, or any custom inference provider. Your data
                                        never leaves your approved environment.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={150} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="pillar-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                            <circle cx="9" cy="7" r="4" />
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Multi-Org Membership & Multi-Tenancy
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Seamlessly work across multiple organizations with org-scoped roles and
                                        permissions. Perfect for agencies, large product groups, or companies
                                        with separate business units — one account, full isolation where it matters.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                        <Col md={4}>
                            <ScrollReveal animation="fade-up" delay={300} className="h-100">
                                <div className="glass-card h-100">
                                    <div className="pillar-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                            <polyline points="9 12 11 14 15 10" />
                                        </svg>
                                    </div>
                                    <h3 style={{ fontSize: '1.15rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                        Configurable Quality Gates
                                    </h3>
                                    <p className="text-muted-white" style={{ fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                                        Set your own rigorous thresholds for spec alignment, code quality,
                                        business value validation, and risk tolerance. Automated gates ensure
                                        nothing ships until it meets the standards your team and compliance
                                        requirements demand.
                                    </p>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                    <Row className="justify-content-center text-center mt-4">
                        <Col lg={8}>
                            <ScrollReveal animation="fade-up" delay={400}>
                                <p className="text-muted-white mb-3" style={{ fontSize: '0.95rem', lineHeight: 1.7 }}>
                                    The exact enterprise-grade controls that product owners, engineering leads,
                                    and security teams expect.
                                </p>
                                <p className="section-subheading mb-4" style={{ fontSize: '0.95rem' }}>
                                    See how QCoda accelerates delivery while keeping every decision traceable.
                                </p>
                                <button onClick={() => setPage('contact')} className="btn-primary-cta">
                                    Request a Demo &rarr;
                                </button>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== INVESTOR SECTION ===== */}
            <section className="site-section investor-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={7} xl={6}>
                            <ScrollReveal animation="scale-in">
                                <div className="glass-card-lg text-center">
                                    <h2 className="section-heading mb-3">For Investors</h2>
                                    <p className="section-subheading mb-4">
                                        QCoda is redefining how software gets built in the age of AI agents.
                                        We're backed by a team with multiple exits and deep domain expertise
                                        in AI, product development, and enterprise tooling.
                                    </p>
                                    <button onClick={() => setPage('contact', 'investor')} className="btn-primary-cta">
                                        Let's Talk
                                    </button>
                                </div>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>

            <hr className="section-divider" />

            {/* ===== FINAL CTA ===== */}
            <section className="site-section-lg" id="waitlist">
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col lg={6}>
                            <ScrollReveal animation="fade-up">
                                <h2 className="section-heading mb-4">Ready to build with alignment?</h2>
                                <div className="form-waitlist mb-4" style={{ backgroundColor: '#f5f7fa', borderRadius: '8px', padding: '1.5rem', color: '#333' }}>
                                    <HubspotForm
                                        portalId="245090272"
                                        formId="efdfa5a6-a51c-46fe-a225-a2d1dab628dd"
                                    />
                                </div>
                                <p className="text-dim" style={{ fontSize: '0.9rem' }}>
                                    Or <button
                                        onClick={() => setPage('contact')}
                                        className="bg-transparent border-0 p-0"
                                        style={{ color: 'rgba(120, 170, 240, 0.9)', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.9rem' }}
                                    >
                                        request a demo
                                    </button> if you'd like a walkthrough.
                                </p>
                            </ScrollReveal>
                        </Col>
                    </Row>
                </Container>
            </section>
        </main>
    );
}

export default Home;
